/**
 * Helpers for the agent integrations view.
 */

/**
 * Window name given to the OAuth popup. Used only as the `window.open` target
 * name — it is NOT a reliable popup marker across the flow: Chromium clears
 * `window.name` on the cross-site OAuth navigations (the popup has no opener,
 * so it switches browsing context group). Popup detection on the landing route
 * uses the `OAUTH_POPUP_REDIRECT_PARAM` query param instead.
 */
const OAUTH_POPUP_WINDOW_NAME = 'slack-oauth';

/**
 * Query-param flag the Google OAuth callback appends to its FE redirect so the
 * landing route knows it is running inside the OAuth popup and should close
 * itself. Survives cross-origin redirects (unlike `window.name`). Value is `1`.
 */
const OAUTH_POPUP_REDIRECT_PARAM = 'oauth_popup';

/**
 * Open a centered placeholder popup window for the OAuth flow.
 *
 * Browsers only allow `window.open` from a user-gesture handler, and they
 * lose that gesture if we `await` before opening. So the click handler opens
 * the popup immediately to `about:blank`, fetches the Slack authorize URL,
 * then navigates this returned handle. We omit `noopener` because we need
 * the handle to set `.location.href`; to keep the reverse-tabnabbing
 * protection `noopener` would have given us, we null `opener` on the popup
 * while it is still same-origin (`about:blank`) so the page it later loads
 * cannot reach back into our window. We still hold the handle, so navigation
 * keeps working.
 *
 * @returns {Window | null} The popup handle, or null if the browser blocked it.
 */
function openOAuthPopup(): Window | null {
  const width = 600;
  const height = 750;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  const popup = window.open(
    'about:blank',
    OAUTH_POPUP_WINDOW_NAME,
    `popup=yes,width=${width},height=${height},left=${left},top=${top}`,
  );
  if (popup) {
    popup.opener = null;
  }
  return popup;
}

export { OAUTH_POPUP_REDIRECT_PARAM, openOAuthPopup };
